import { Typography, Box, Paper, CircularProgress } from '@mui/material';
import React, { useState, useEffect } from 'react';
import {
  FieldLabel,
  StyledButton,
  StyledTextField,
} from '../Configuration/configurationForms/styledFormComponents';
import { ImageUpload } from '../CompanyInformation/ImageUpload';
import _ from 'lodash';
import { GamifyToast } from 'application/common/CustomToasts';
import { API, graphqlOperation } from 'aws-amplify';
import { updateUser } from 'graphql/mutations';
import { validateEmail, validatePhoneNumber } from '../../utils/validation';
import { useCurrentUserStore } from 'application/store/zustand/useCurrentUserStore';
import { useTheme } from '../../../../context/PermissionsContext/ThemeContext/ThemeContext';

export default function CustomerFacingProfile() {
  const [user, setUser] = useState<any>(null);
  const [originalUser, setOriginalUser] = useState<any>(null);
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errors, setErrors] = useState<{
    externalEmail?: string;
    externalPhoneNumber?: string;
  }>({});

  const { theme } = useTheme();
  const userID =
    useCurrentUserStore((state) => state.userID) || (global as any)?.me?.id;

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userID) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const query = `
          query GetUser($id: ID!) {
            getUser(id: $id) {
              id
              name
              email
              phoneNumber
              avatarID
              externalPhoneNumber
              externalTitle
              externalName
              externalEmail
            }
          }
        `;

        const res: any = await API.graphql({
          query,
          variables: { id: userID },
        });

        const userData = res.data.getUser;
        if (userData) {
          setUser({
            ...userData,
            externalName: userData.externalName || '',
            externalEmail: userData.externalEmail || '',
          });
          setOriginalUser({
            ...userData,
            externalName: userData.externalName || '',
            externalEmail: userData.externalEmail || '',
          });
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setUser({
          id: userID,
          name: '',
          email: '',
          phoneNumber: '',
          avatarID: null,
          externalName: '',
          externalEmail: '',
          externalPhoneNumber: '',
          externalTitle: '',
        });
        setOriginalUser({
          id: userID,
          name: '',
          email: '',
          phoneNumber: '',
          avatarID: null,
          externalName: '',
          externalEmail: '',
          externalPhoneNumber: '',
          externalTitle: '',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [userID]);

  useEffect(() => {
    if (!_.isEqual(user, originalUser)) {
      setHasChanged(true);
    } else if (hasChanged) {
      setHasChanged(false);
    }
  }, [user]);

  const validateFields = (): boolean => {
    const newErrors: { externalEmail?: string; externalPhoneNumber?: string } =
      {};

    if (user?.externalEmail && !validateEmail(user.externalEmail)) {
      newErrors.externalEmail = 'Please enter a valid email address';
    }

    if (
      user?.externalPhoneNumber &&
      !validatePhoneNumber(user.externalPhoneNumber)
    ) {
      newErrors.externalPhoneNumber =
        'Please enter a valid phone number (at least 10 digits)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      GamifyToast.error('Please fix validation errors before saving');
      return;
    }

    try {
      await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: user.id,
            externalName: user.externalName,
            externalEmail: user.externalEmail,
            externalPhoneNumber: user.externalPhoneNumber,
            externalTitle: user.externalTitle,
            avatarID: user.avatarID,
          },
        })
      );

      setOriginalUser(user);
      setHasChanged(false);
      GamifyToast.success('Customer-facing profile updated successfully');
    } catch (err) {
      console.error('Error updating user:', err);
      GamifyToast.error('Error updating customer-facing profile');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (!userID || !user) {
    if (!userID) {
      return (
        <Typography color="error" sx={{ p: 2 }}>
          Unable to load user information. Please refresh the page.
        </Typography>
      );
    }
    return null;
  }

  const renderSection = (
    title: string,
    subtitle: string,
    children: React.ReactNode
  ) => (
    <Box sx={{ mb: 4 }}>
      <Typography
        sx={{ fontWeight: 600, fontSize: 16, mb: 0.5, color: theme.text }}
      >
        {title}
      </Typography>
      <Typography sx={{ fontSize: 14, color: theme.textLight, mb: 2 }}>
        {subtitle}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.background1,
        borderRadius: '16px',
        marginBottom: 2,
      }}
    >
      <Typography sx={{ fontWeight: 780, fontSize: 20, color: theme.text }}>
        Customer-Facing Profile
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 15,
          color: theme.textLight,
          marginBottom: 3,
        }}
      >
        Manage the information that customers see when interacting with you.
      </Typography>

      {renderSection(
        'Profile Information',
        'This information will be visible to your customers',
        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
          <Box>
            <FieldLabel>Profile Image</FieldLabel>
            <ImageUpload
              value={user.avatarID}
              onChange={(imageKey) => setUser({ ...user, avatarID: imageKey })}
              label="Profile Image"
            />
          </Box>
          <Box>
            <FieldLabel>Name</FieldLabel>
            <StyledTextField
              fullWidth
              value={user.externalName || ''}
              onChange={(e) =>
                setUser({ ...user, externalName: e.target.value })
              }
              placeholder={user.name || 'Enter name'}
            />
            <Typography sx={{ fontSize: 12, color: theme.textLight, mt: 0.5 }}>
              Leave blank to use your internal name: {user.name}
            </Typography>
          </Box>
          <Box>
            <FieldLabel>Phone Number</FieldLabel>
            <StyledTextField
              fullWidth
              value={user.externalPhoneNumber || ''}
              onChange={(e) =>
                setUser({ ...user, externalPhoneNumber: e.target.value })
              }
              placeholder={user.phoneNumber || 'Enter phone number'}
              error={!!errors.externalPhoneNumber}
              helperText={errors.externalPhoneNumber}
            />
            <Typography sx={{ fontSize: 12, color: theme.textLight, mt: 0.5 }}>
              Leave blank to use your internal phone: {user.phoneNumber}
            </Typography>
          </Box>
          <Box>
            <FieldLabel>Email</FieldLabel>
            <StyledTextField
              fullWidth
              type="email"
              value={user.externalEmail || ''}
              onChange={(e) =>
                setUser({ ...user, externalEmail: e.target.value })
              }
              placeholder={user.email || 'Enter email'}
              error={!!errors.externalEmail}
              helperText={errors.externalEmail}
            />
            <Typography sx={{ fontSize: 12, color: theme.textLight, mt: 0.5 }}>
              Leave blank to use your internal email: {user.email}
            </Typography>
          </Box>
          <Box>
            <FieldLabel>Role</FieldLabel>
            <StyledTextField
              fullWidth
              value={user.externalTitle || ''}
              onChange={(e) =>
                setUser({ ...user, externalTitle: e.target.value })
              }
              placeholder="e.g., Senior Estimator, Project Manager"
            />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          mt: 2,
        }}
      >
        <StyledButton disabled={!hasChanged} onClick={handleSubmit}>
          Save Changes
        </StyledButton>
      </Box>
    </Paper>
  );
}
